import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { authConfig, getAuthorizeEndpoint, getLogoutEndpoint } from '../config/auth';
import { environment } from '../config/environment';

interface User {
  email?: string;
  name?: string;
  given_name?: string;
  family_name?: string;
  [key: string]: any;
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  signIn: () => void;
  signOut: () => void;
  loading: boolean;
  getValidToken: () => Promise<string | null>;
  refreshUserFromToken: () => Promise<User | null>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  const getQueryParam = (name: string): string | null => {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get(name);
  };

  const exchangeCodeForToken = useCallback(async (code: string) => {
    console.log('[AUTH DEBUG] Starting token exchange with code:', code.substring(0, 10) + '...');
    console.log('[AUTH DEBUG] BFF endpoint:', environment.api.bff);
    console.log('[AUTH DEBUG] Redirect URI:', authConfig.redirectUri);

    const body = JSON.stringify({
      code,
      redirect_uri: authConfig.redirectUri
    });

    try {
      const resp = await fetch(`${environment.api.bff}/auth/login`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json"
        },
        credentials: 'include',
        body
      });

      console.log('[AUTH DEBUG] BFF response status:', resp.status);
      console.log('[AUTH DEBUG] BFF response headers:', Object.fromEntries(resp.headers.entries()));

      if (!resp.ok) {
        const errorText = await resp.text();
        console.error('[AUTH DEBUG] Token exchange failed:', resp.status, errorText);
        throw new Error(`Token exchange failed: ${resp.status} ${errorText}`);
      }

      const data = await resp.json();
      console.log('[AUTH DEBUG] Token exchange successful, response keys:', Object.keys(data));
      console.log('[AUTH DEBUG] Has id_token:', !!data.id_token, 'Has access_token:', !!data.access_token);
      return data;
    } catch (error) {
      console.error('[AUTH DEBUG] Token exchange error:', error);
      throw error;
    }
  }, []);

  const decodeJwt = useCallback((token: string): User => {
    const payload = token.split(".")[1];
    return JSON.parse(atob(payload.replace(/-/g, "+").replace(/_/g, "/")));
  }, []);

  const isTokenExpired = useCallback((token: string): boolean => {
    try {
      const decoded = decodeJwt(token);
      const currentTime = Date.now() / 1000;
      return decoded.exp ? decoded.exp < currentTime : true;
    } catch {
      return true;
    }
  }, [decodeJwt]);

  const isTokenExpiringSoon = useCallback((token: string, bufferMinutes: number = 5): boolean => {
    try {
      const decoded = decodeJwt(token);
      const currentTime = Date.now() / 1000;
      const bufferSeconds = bufferMinutes * 60;
      return decoded.exp ? decoded.exp < (currentTime + bufferSeconds) : true;
    } catch {
      return true;
    }
  }, [decodeJwt]);

  const signOut = async () => {
    console.log('[AUTH DEBUG] Starting sign out process...');

    // Clear local state and storage FIRST
    setUser(null);
    localStorage.removeItem('idToken');
    localStorage.removeItem('accessToken');
    localStorage.removeItem('returnPath');
    console.log('[AUTH DEBUG] Cleared local state and storage');

    try {
      // Call backend logout endpoint for proper session cleanup
      console.log('[AUTH DEBUG] Calling backend logout...');
      await fetch(`${environment.api.bff}/auth/logout`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: 'include'
      });
      console.log('[AUTH DEBUG] Backend logout successful');
    } catch (error) {
      console.error('[AUTH DEBUG] Backend logout failed, but continuing:', error);
    }

    // Redirect to Cognito logout
    const logoutUrl = getLogoutEndpoint();
    console.log('[AUTH DEBUG] Redirecting to:', logoutUrl);
    window.location.href = logoutUrl;
  };

  const refreshToken = useCallback(async (): Promise<{ token: string; user: User } | null> => {
    console.log('[AUTH DEBUG] Attempting token refresh...');
    console.log('[AUTH DEBUG] All cookies:', document.cookie);
    console.log('[AUTH DEBUG] Refresh endpoint:', `${environment.api.bff}/auth/refresh`);
    try {
      const response = await fetch(`${environment.api.bff}/auth/refresh`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json"
        },
        credentials: 'include'
      });

      console.log('[AUTH DEBUG] Refresh response status:', response.status);
      console.log('[AUTH DEBUG] Refresh response headers:', Object.fromEntries(response.headers.entries()));

      if (!response.ok) {
        const errorText = await response.text();
        console.error('[AUTH DEBUG] Token refresh failed:', response.status, errorText);
        throw new Error(`Token refresh failed: ${response.status}`);
      }

      const data = await response.json();
      console.log('[AUTH DEBUG] Refresh response keys:', Object.keys(data));
      const newIdToken = data.id_token;
      const newAccessToken = data.access_token;

      if (!newIdToken || !newAccessToken) {
        console.error('[AUTH DEBUG] Missing tokens in refresh response - id_token:', !!newIdToken, 'access_token:', !!newAccessToken);
        throw new Error('Missing tokens in refresh response');
      }

      // Update stored tokens
      localStorage.setItem('idToken', newIdToken);
      localStorage.setItem('accessToken', newAccessToken);
      console.log('[AUTH DEBUG] New tokens stored in localStorage');

      const decodedUser = data.user;
      setUser(decodedUser);
      console.log('[AUTH DEBUG] User updated from refresh:', decodedUser?.email);

      return { token: newIdToken, user: decodedUser };
    } catch (error) {
      console.error('[AUTH DEBUG] Token refresh failed, signing out user', error);
      // If refresh fails, sign out the user
      signOut();
      return null;
    }
  }, []);

  const getValidToken = async (): Promise<string | null> => {
    const storedToken = localStorage.getItem('idToken');

    if (!storedToken) {
      return null;
    }

    // Check if token is expired or expiring soon
    if (isTokenExpired(storedToken)) {
      console.log('Token is expired, attempting refresh...');
      const result = await refreshToken();
      return result?.token || null;
    }

    if (isTokenExpiringSoon(storedToken)) {
      console.log('Token is expiring soon, attempting refresh...');
      const result = await refreshToken();
      return result?.token || storedToken; // Fallback to current token if refresh fails
    }

    return storedToken;
  };

  const refreshUserFromToken = useCallback(async (): Promise<User | null> => {
    console.log('[AUTH DEBUG] Refreshing user from token...');
    const result = await refreshToken();
    if (!result) {
      console.error('[AUTH DEBUG] Failed to refresh user from token');
      return null;
    }
    return result.user;
  }, [refreshToken]);

  const signIn = () => {
    // Save current path for return after login
    const currentPath = window.location.pathname + window.location.search;
    if (currentPath !== '/') {
      localStorage.setItem('returnPath', currentPath);
    }
    window.location.href = getAuthorizeEndpoint();
  };

  const initializeFromStoredToken = useCallback(() => {
    console.log('[AUTH DEBUG] Initializing from stored token...');
    const storedToken = localStorage.getItem('idToken');
    console.log('[AUTH DEBUG] Stored token exists:', !!storedToken);

    if (storedToken) {
      console.log('[AUTH DEBUG] Token length:', storedToken.length);
      const expired = isTokenExpired(storedToken);
      console.log('[AUTH DEBUG] Token expired:', expired);

      if (!expired) {
        try {
          const decodedUser = decodeJwt(storedToken);
          console.log('[AUTH DEBUG] Successfully decoded user:', decodedUser?.email);
          setUser(decodedUser);
          setLoading(false);
          return true;
        } catch (e) {
          console.error("[AUTH DEBUG] Failed to decode stored token", e);
          localStorage.removeItem('idToken');
        }
      } else {
        console.log('[AUTH DEBUG] Token expired, removing from storage');
        localStorage.removeItem('idToken');
        localStorage.removeItem('accessToken');
      }
    }
    return false;
  }, [isTokenExpired, decodeJwt]);

  const handleOAuthCallback = useCallback(async () => {
    console.log('[AUTH DEBUG] Checking for OAuth callback...');
    const code = getQueryParam("code");
    console.log('[AUTH DEBUG] OAuth code present:', !!code);

    if (code) {
      console.log('[AUTH DEBUG] Processing OAuth callback with code:', code.substring(0, 10) + '...');

      window.history.replaceState({}, document.title, authConfig.redirectUri);

      try {
        const data = await exchangeCodeForToken(code);
        console.log('[AUTH DEBUG] Exchange response data keys:', Object.keys(data));
        const idToken = data.id_token;
        const accessToken = data.access_token;
        console.log('[AUTH DEBUG] ID token present:', !!idToken, 'Access token present:', !!accessToken);

        if (!idToken || !accessToken) {
          console.error('[AUTH DEBUG] Missing tokens in response:', data);
          throw new Error("Missing tokens in response");
        }

        const decodedUser = decodeJwt(idToken);
        console.log('[AUTH DEBUG] Decoded user from new token:', decodedUser?.email);

        localStorage.setItem('idToken', idToken);
        localStorage.setItem('accessToken', accessToken);
        console.log('[AUTH DEBUG] New tokens stored in localStorage');
        setUser(decodedUser);
        console.log('[AUTH DEBUG] User state updated after OAuth callback');

        // Check for saved return path and redirect
        const returnPath = localStorage.getItem('returnPath');
        if (returnPath && returnPath !== '/') {
          console.log('[AUTH DEBUG] Redirecting to saved return path:', returnPath);
          localStorage.removeItem('returnPath');
          window.location.replace(returnPath);
        }

        return true;
      } catch (e) {
        console.error("[AUTH DEBUG] OAuth callback failed", e);
        setUser(null);
      }
    } else {
      console.log('[AUTH DEBUG] No OAuth code found in URL parameters');
    }
    return false;
  }, [exchangeCodeForToken, decodeJwt]);

  // Initialize authentication state on mount
  useEffect(() => {
    const initializeAuth = async () => {
      console.log('[AUTH DEBUG] Initializing authentication...');
      console.log('[AUTH DEBUG] Current URL:', window.location.href);
      console.log('[AUTH DEBUG] Current pathname:', window.location.pathname);
      console.log('[AUTH DEBUG] Current search params:', window.location.search);

      // Skip auto-login on logout success page
      if (window.location.pathname === '/logout-success') {
        console.log('[AUTH DEBUG] On logout success page, skipping all auth initialization');
        setUser(null);
        localStorage.removeItem('idToken');
        localStorage.removeItem('accessToken');
        setLoading(false);
        return;
      }

      // Try to initialize from stored token
      console.log('[AUTH DEBUG] Attempting to initialize from stored token...');
      if (initializeFromStoredToken()) {
        console.log('[AUTH DEBUG] Successfully initialized from stored token');
        return;
      }

      // Handle OAuth callback if present
      console.log('[AUTH DEBUG] Attempting OAuth callback handling...');
      const callbackSuccess = await handleOAuthCallback();
      console.log('[AUTH DEBUG] OAuth callback result:', callbackSuccess);

      setLoading(false);
      console.log('[AUTH DEBUG] Authentication initialization complete');
    };

    initializeAuth();
  }, [initializeFromStoredToken, handleOAuthCallback]);

  return (
    <AuthContext.Provider value={{
      user,
      isAuthenticated: !!user,
      signIn,
      signOut,
      loading,
      getValidToken,
      refreshUserFromToken
    }}>
      {children}
    </AuthContext.Provider>
  );
};